<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <v-row
      v-for="lang in teamLangs"
      :key="lang"
    >
      <v-col
        v-if="selectedLang === lang"
      >
        <VTextField
          v-model="data.name[lang]"
          required="lang === teamMainLang"
          :label="$t(`forms.labels.name`)"
          :rules="{ required: lang === teamMainLang, min: 1, max: 200 }"
          :disabled="!isEditable"
          data-cy="nameAccessGroup"
        />
      </v-col>
    </v-row>
    <VAutocomplete
      v-model="data.clients"
      :label="$t('forms.labels.clients')"
      :items="displayedClients"
      :loading="clientsLoading"
      :disabled="!isEditable"
      cacheItems
      multiple
      datacy="clientAccessGroup"
      @search="debouncedClientSearch"
    />
    <VAutocomplete
      ref="autocompleteRef"
      v-model="data.members"
      :label="$t('forms.labels.members')"
      :items="displayedMembersAndGroupLeaders"
      :loading="membersLoading"
      cacheItems
      multiple
      :disabled="!isEditable"
      datacy="membersAccessGroup"
      @search="debouncedMemberSearch"
    />
    <VAutocomplete
      ref="autocompleteRef"
      v-model="data.groupLeaders"
      :label="$t('forms.labels.groupLeaders')"
      :items="displayedMembersAndGroupLeaders"
      :loading="membersLoading"
      cacheItems
      multiple
      :disabled="!isEditable"
      datacy="groupLeadersAccessGroup"
      @search="debouncedMemberSearch"
    />
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirtyChanged', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('validChanged', valid)"
    />
  </ValidationObserver>
</template>

<script>
import { debounce } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import roles from '@/utilities/enums/rolesTypes';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { mapGetters } from 'vuex';

export default {
    components: {
        ValidationObserver,
        SlotScopeWatcher,
        VAutocomplete,
        VTextField,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            membersLoading: false,
            clientsLoading: false,
            members: [],
            clients: [],
            debouncedClientSearch: debounce(this.searchClients, 500),
            debouncedMemberSearch: debounce(this.searchMembers, 500),
            data: {
                name: {},
                clients: [],
                members: [],
                groupLeaders: [],
            },
            search: {
                clients: '',
                members: '',
            },
            queryParams: {
                members: { page: 1, perpage: 25 },
            },
        };
    },
    computed: {
        ...mapGetters([
            'teamLangs',
            'teamMainLang',
            'selectedLang',
        ]),
        displayedClients() {
            return this.clients.map(({ id, name, accountNumber }) => ({
                id,
                name: `${name} [${accountNumber}]`,
            }));
        },
        displayedMembersAndGroupLeaders() {
            const adminsAndManagers = [
                roles.ADMINISTRATOR,
                roles.MANAGER,
                roles.LABMANAGER,
                roles.LABMICROBIOLOGIST,
            ];
            return this.members.length ? this.members
                .filter(member => !adminsAndManagers.includes(member.roles.key))
                .map(({ id, firstName, lastName, email }) => ({
                    id,
                    name: `${firstName} ${lastName} (${email})`,
                })) : [];
        },
        isEdition() {
            return !this.populateWith.empty;
        },
    },
    watch: {
        populateWith: {
            handler() {
                if (this.isEdition) {
                    this.data = this.populateWith;
                    this.data.clients = this.populateWith.clients
                        ? this.populateWith.clients.map(({ id }) => id)
                        : [];
                    this.data.members = this.populateWith.members
                        ? this.populateWith.members.map(({ id }) => id)
                        : [];
                    this.data.groupLeaders = this.populateWith.groupLeaders
                        ? this.populateWith.groupLeaders.map(({ id }) => id)
                        : [];
                }
            },
            immediate: true,
        },
        data: {
            handler() {
                this.updateData();
            },
            deep: true,
        },
    },
    async created() {
        await Promise.all([
            this.getMembers(),
            this.getClients(),
        ]);
    },
    methods: {
        updateData() {
            this.$emit('sendData', this.data);
        },
        async searchClients(search) {
            this.search.clients = search;
            this.getClients();
        },
        async searchMembers(search) {
            this.search.members = search;
            this.getMembers();
        },
        async getClients() {
            this.clientsLoading = true;
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({ search: this.search.clients, allClients: true }, true);
            const { data } = await this.$rq.getClients(cleanQueryParams);
            this.clients = data.clients;
            this.clientsLoading = false;
        },
        async getMembers() {
            const arrayRoles = [roles.SALESREP, roles.MANAGER, roles.SERVICETECH, roles.SALESMANAGER];
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({ search: this.search.members, allTeamMembers: true }, true);
            const { data } = await this.$rq.getTeamMembers(this.$store.getters.teamID, arrayRoles, cleanQueryParams);
            this.queryParams.members = {
                ...this.queryParams.members,
                ...data.pagination,
            };
            this.members = data.members;
        },
    },
};
</script>
